import { defineStore } from 'pinia'
import contractService from '@/services/contract.service'
import type { Contract, ContractFilters } from '@/types/contract.types'
import type { PaginationMeta, SortOrder } from '@/types/api.types'

interface ContractsState {
  contracts: Contract[]
  selectedContract: Contract | null
  loading: boolean
  filters: ContractFilters
  pagination: PaginationMeta
}

export const useContractsStore = defineStore('contracts', {
  state: (): ContractsState => ({
    contracts: [],
    selectedContract: null,
    loading: false,
    filters: { sort_order: 'desc' },
    pagination: {
      current_page: 1,
      per_page: 10,
      total: 0,
      last_page: 1
    }
  }),

  getters: {
    getContractById: (state) => (id: string) => {
      return state.contracts.find(c => c.id === id)
    },

    activeContracts: (state) => {
      return state.contracts.filter(c => c.status === 'active')
    }
  },

  actions: {
    async fetchContracts(page: number = 1) {
      this.loading = true
      try {
        const response = await contractService.getContracts({
          ...this.filters,
          page,
          page_size: this.pagination.per_page
        })
        this.contracts = response.data
        this.pagination = response.meta
      } finally {
        this.loading = false
      }
    },

    async fetchContract(id: string) {
      this.loading = true
      try {
        this.selectedContract = await contractService.getContract(id)
      } finally {
        this.loading = false
      }
    },

    async createContract(data: any) {
      const contract = await contractService.createContract(data)
      this.contracts.unshift(contract)
      return contract
    },

    async updateContract(id: string, data: any) {
      const contract = await contractService.updateContract(id, data)
      const index = this.contracts.findIndex(c => c.id === id)
      if (index !== -1) {
        this.contracts[index] = contract
      }
      if (this.selectedContract?.id === id) {
        this.selectedContract = contract
      }
      return contract
    },

    async deleteContract(id: string) {
      await contractService.deleteContract(id)
      this.contracts = this.contracts.filter(c => c.id !== id)
      if (this.selectedContract?.id === id) {
        this.selectedContract = null
      }
    },

    async uploadAttachment(id: string, file: File) {
      const attachment = await contractService.uploadAttachment(id, file)
      if (this.selectedContract?.id === id) {
        this.selectedContract.attachments.push(attachment)
      }
      return attachment
    },

    async deleteAttachment(contractId: string, attachmentId: string | number) {
      const attachmentKey = String(attachmentId);
      await contractService.deleteAttachment(contractId, attachmentKey);
      if (this.selectedContract?.id === contractId) {
        this.selectedContract.attachments = this.selectedContract.attachments.filter(
          (a) => String(a.id) !== attachmentKey,
        );
      }
    },

    async updateStatus(id: string, status: string) {
      const contract = await contractService.updateStatus(id, status)
      const index = this.contracts.findIndex(c => c.id === id)
      if (index !== -1) {
        this.contracts[index] = contract
      }
      return contract
    },

    setFilters(filters: ContractFilters) {
      this.filters = filters
    },

    setSort(sortBy: string, sortOrder: SortOrder) {
      this.filters.sort_by = sortBy
      this.filters.sort_order = sortOrder
      this.pagination.current_page = 1
    },

    clearFilters() {
      this.filters = {}
    }
  }
})
